import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { parse, format, isValid } from 'date-fns';
import { tap } from 'rxjs/operators';
import { MatFormField, MatLabel, MatHint, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

export type TimeSelectorFieldConfig = FormlyFieldConfig & {
    props: {
        format?: string;
    };
};

@Component({
    templateUrl: './time-selector.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatLabel, MatInput, ReactiveFormsModule, MatHint, MatError]
})
export class TimeSelectorComponent extends FieldType<TimeSelectorFieldConfig> implements OnInit {
    private readonly destroyRef = inject(DestroyRef);

    public timeControl = new FormControl<string>('');
    public readonly defaultFormat = 'HH:mm';

    constructor() {
        super();
    }

    public get initialValue() {
        if (this.formControl.value && typeof this.formControl.value === 'string') {
            return this.formControl.value;
        } else if (this.props.required) {
            return format(new Date(), this.format);
        }
        return '';
    }

    public get format() {
        if (this.props.format && typeof this.props.format === 'string') {
            return this.props.format;
        }
        return this.defaultFormat;
    }

    public get step() {
        const sections = this.format.split(':');
        switch (sections.length) {
            case 3:
                return 1;
            case 2:
            default:
                return undefined;
        }
    }

    ngOnInit() {
        this.setInitialFormState();
        this.setFormValidators();
        this.initializeValueChangeHandlers();
    }

    private setInitialFormState() {
        const initialValue = this.initialValue;
        this.timeControl.setValue(initialValue);
        this.formControl.setValue(initialValue);
    }

    private setFormValidators() {
        const validators = [this.getFormatValidator(this.format)];
        if (this.props.required) {
            validators.push(Validators.required);
        }
        this.timeControl.setValidators(validators);
        this.updateParentValidators(validators);
    }

    private getFormatValidator(format: string): ValidatorFn {
        return (control: FormControl<string>) => {
            const value = control.value,
                mockFiller = new Date(),
                asDate = parse(value, format, mockFiller);
            if (asDate && isValid(asDate)) {
                return null;
            }
            return { invalidTimeFormat: `Input does not match format: ${format}` };
        };
    }

    private initializeValueChangeHandlers(): void {
        this.timeControl.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((time: string) => {
                    console.debug(time);
                    this.formControl.setValue(time);
                })
            )
            .subscribe();
    }

    private updateParentValidators(validators: ValidatorFn | ValidatorFn[]) {
        setTimeout(() => {
            this.formControl.setValidators(validators);
            this.formControl.updateValueAndValidity();
        });
    }
}
